"use client"

import { useState, useContext, useEffect, useRef } from "react"
import { ErpContext } from "../../context/ErpContext.js"
import {
  Button,
  Typography,
  Container,
  Modal,
  Box,
  CircularProgress,
  IconButton,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import SaveIcon from "@mui/icons-material/Save"
import PersonIcon from "@mui/icons-material/Person"
import FormularioEntidadComponent from "./FormularioEntidadComponent.js"

const CrearEntidadClienteModalComponent = ({ open, handleClose, entidad, isEditing, handleAlerta, onClienteCreado, tipoEntidad = "cliente" }) => {
  const { tipoEntidades, getTipoEntidad, postEntidad, updateEntidad, condicionIva, getCondicionIva, getCondicionIvaByEntidad, isNavbarCollapsed } = useContext(ErpContext)

  // Estados para los campos del formulario
  const [descripcionEntidad, setDescripcionEntidad] = useState("")
  const [apellidoEntidad, setApellidoEntidad] = useState("")
  const [telefonoEntidad, setTelefonoEntidad] = useState("")
  const [emailEntidad, setEmailEntidad] = useState("")
  const [dniCuitCuilEntidad, setdniCuitCuilEntidad] = useState("")
  const [direccionEntidad, setDireccionEntidad] = useState("")
  const [localidadEntidad, setLocalidadEntidad] = useState("")
  const [provinciaEntidad, setProvinciaEntidad] = useState("")
  const [idTipoEntidadEntidad, setIdTipoEntidadEntidad] = useState("")
  const [idCondicionIvaEntidad, setIdCondicionIvaEntidad] = useState("")

  // Estados adicionales para UI
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const isFirstRender = useRef(true)

  // Etiquetas dinámicas según el tipo de entidad
  const etiquetas = {
    cliente: {
      crear: "Crear Nuevo Cliente",
      actualizar: "Actualizar Cliente"
    },
    proveedor: {
      crear: "Crear Nuevo Proveedor",
      actualizar: "Actualizar Proveedor"
    }
  }

  const labels = etiquetas[tipoEntidad]

  // Mapeo de tipo de entidad según contexto
  const tipoEntidadPorContexto = {  // *HARDCODED*
    cliente: 1, // ID para Cliente
    proveedor: 2 // ID para Proveedor
  }

  useEffect(() => {
    if (!open) return

    if (tipoEntidades.length === 0) {
      getTipoEntidad()
    }

    if (condicionIva.length === 0) {
      getCondicionIva()
    }
  }, [open, tipoEntidades.length, condicionIva.length, getTipoEntidad, getCondicionIva])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return // Evita la ejecución en el primer render
    }

    const cargarDatosEntidad = async () => {
      if (entidad) {
        // Cuando la entidad cambia, actualizar los estados de los campos
        setDescripcionEntidad(entidad.descripcion || "")
        setApellidoEntidad(entidad.apellido || "")
        setTelefonoEntidad(entidad.telefono || "")
        setEmailEntidad(entidad.email || "")
        setdniCuitCuilEntidad(entidad.dniCuitCuil || "")
        setDireccionEntidad(entidad.direccion || "")
        setLocalidadEntidad(entidad.localidad || "")
        setProvinciaEntidad(entidad.provincia || "")
        setIdTipoEntidadEntidad(entidad.idTipoEntidad || "")

        // Obtener la condición IVA desde el backend
        if (entidad.id && isEditing) {
          try {
            const condicionIvaData = await getCondicionIvaByEntidad(entidad.id)
            setIdCondicionIvaEntidad(condicionIvaData?.idCondicionIva || "")
          } catch (error) {
            console.error("Error obteniendo condición IVA:", error)
            setIdCondicionIvaEntidad("")
          }
        } else {
          setIdCondicionIvaEntidad(entidad.idCondicionIva || "")
        }
      } else {
        // Limpiar los campos si no hay entidad
        resetForm()
      }
    }

    cargarDatosEntidad()
  }, [entidad, isEditing, getCondicionIvaByEntidad]) // Este efecto se ejecutará cada vez que `entidad` cambie

  // Función para validar el formulario
  const validateForm = () => {
    const newErrors = {}

    if (!descripcionEntidad.trim()) {
      newErrors.descripcion = "El nombre es requerido"
    }

    if (!dniCuitCuilEntidad.trim()) {
      newErrors.dniCuitCuil = "El DNI/CUIT/CUIL es requerido"
    }

    if (emailEntidad && !/\S+@\S+\.\S+/.test(emailEntidad)) {
      newErrors.email = "Email inválido"
    }

    if (!idTipoEntidadEntidad) {
      newErrors.idTipoEntidad = "Debe seleccionar un tipo de entidad"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Función para resetear el formulario
  const resetForm = () => {
    setDescripcionEntidad("")
    setApellidoEntidad("")
    setTelefonoEntidad("")
    setEmailEntidad("")
    setdniCuitCuilEntidad("")
    setDireccionEntidad("")
    setLocalidadEntidad("")
    setProvinciaEntidad("")
    setIdTipoEntidadEntidad("")
    setIdCondicionIvaEntidad("")
    setErrors({})
  }

  const handleEntidadSubmit = async (e) => {
    e.preventDefault()
    e.stopPropagation() // Evita que el evento se propague al formulario padre

    // Validar formulario
    if (!validateForm()) {
      return
    }

    try {
      setIsLoading(true)

      if (isEditing) {
        // Si es una edición, actualiza la entidad existente
        await updateEntidad(
          entidad.id,
          descripcionEntidad,
          apellidoEntidad,
          telefonoEntidad,
          emailEntidad,
          dniCuitCuilEntidad,
          direccionEntidad,
          localidadEntidad,
          provinciaEntidad,
          idTipoEntidadEntidad,
          idCondicionIvaEntidad,
        )
      } else {
        // Si no, crea una nueva entidad
        const clienteCreado = await postEntidad(
          descripcionEntidad,
          apellidoEntidad,
          telefonoEntidad,
          emailEntidad,
          dniCuitCuilEntidad,
          direccionEntidad,
          localidadEntidad,
          provinciaEntidad,
          idTipoEntidadEntidad,
          idCondicionIvaEntidad,
        )

        // Si se creó un cliente nuevo y existe la función callback, llamarla
        if (clienteCreado && onClienteCreado) {
          const clienteData = {
            id: clienteCreado.id,
            label: `${descripcionEntidad} ${apellidoEntidad} ${dniCuitCuilEntidad}`,
            descripcion: descripcionEntidad,
            apellido: apellidoEntidad,
            dniCuitCuil: dniCuitCuilEntidad,
            telefono: telefonoEntidad,
            email: emailEntidad,
            direccion: direccionEntidad,
            localidad: localidadEntidad,
            provincia: provinciaEntidad,
            idTipoEntidad: idTipoEntidadEntidad,
            idCondicionIva: idCondicionIvaEntidad,
          }
          onClienteCreado(clienteData)
        }
      }

      // Limpiar los campos después de la acción
      resetForm()
      handleAlerta(true)
      handleClose()
    } catch (error) {
      console.log("error")
      handleAlerta(false)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <div
        className="modalContenido"
        style={{
          backgroundColor: "white",
          borderRadius: "16px",
          padding: "0",
          width: "80%",
          maxWidth: "800px",
          maxHeight: "90vh",
          overflow: "auto",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
        }}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: "#5b5b5f",
            color: "white",
            padding: "16px 24px",
            borderTopLeftRadius: "16px",
            borderTopRightRadius: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <PersonIcon style={{ marginRight: "12px", color: "rgb(244, 177, 14)" }} />
            <Typography variant="h6" style={{ fontWeight: "500" }}>
              {isEditing ? labels.actualizar : labels.crear}
            </Typography>
          </div>
          <IconButton onClick={handleClose} style={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </div>

        <Container className="contenedorModal containerModuloDiseñoGeneral" style={{ padding: "24px" }}>
          <form onSubmit={handleEntidadSubmit}>
            <FormularioEntidadComponent
              descripcionEntidad={descripcionEntidad}
              setDescripcionEntidad={setDescripcionEntidad}
              apellidoEntidad={apellidoEntidad}
              setApellidoEntidad={setApellidoEntidad}
              dniCuitCuilEntidad={dniCuitCuilEntidad}
              setdniCuitCuilEntidad={setdniCuitCuilEntidad}
              telefonoEntidad={telefonoEntidad}
              setTelefonoEntidad={setTelefonoEntidad}
              emailEntidad={emailEntidad}
              setEmailEntidad={setEmailEntidad}
              direccionEntidad={direccionEntidad}
              setDireccionEntidad={setDireccionEntidad}
              localidadEntidad={localidadEntidad}
              setLocalidadEntidad={setLocalidadEntidad}
              provinciaEntidad={provinciaEntidad}
              setProvinciaEntidad={setProvinciaEntidad}
              idTipoEntidadEntidad={idTipoEntidadEntidad}
              setIdTipoEntidadEntidad={setIdTipoEntidadEntidad}
              idCondicionIvaEntidad={idCondicionIvaEntidad}
              setIdCondicionIvaEntidad={setIdCondicionIvaEntidad}
              tipoEntidades={tipoEntidades}
              condicionIva={condicionIva}
              errors={errors}
              showCondicionIva={true}
              compact={true}
              tipoEntidadReadonly={true}
              tipoEntidadPredeterminado={tipoEntidadPorContexto[tipoEntidad]}
            />

            {/* Botones de acción */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 4,
                gap: 2,
              }}
            >
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={isLoading}
                style={{
                  padding: "10px 24px",
                  borderRadius: "8px",
                  fontWeight: "500",
                  borderWidth: "2px",
                  borderColor: "#5b5b5f",
                  color: "#5b5b5f",
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                startIcon={isLoading ? null : <SaveIcon />}
                disabled={isLoading}
                style={{
                  padding: "10px 24px",
                  borderRadius: "8px",
                  fontWeight: "500",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                  backgroundColor: "#5b5b5f",
                }}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : isEditing ? "Actualizar" : "Guardar"}
              </Button>
            </Box>
          </form>
        </Container>

        {isLoading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10,
              borderRadius: "16px",
            }}
          >
            <CircularProgress size={60} thickness={4} />
          </div>
        )}
      </div>
    </Modal>
  )
}

export default CrearEntidadClienteModalComponent
